export interface StaticConfig {
	app_name: string;
	domain: string;
	api: string;
	debug: boolean;
	library_name: string;
}

export interface AlbumCounts {
	albums: number;
	media: number;
	issues: number;
}

export interface AppSummary {
	ok: boolean;
	app_name: string;
	library_name: string;
	counts: AlbumCounts;
	scan_status: unknown;
}

export interface AdminSession {
	authenticated: boolean;
	expires_at: number | null;
}

export interface AdminStatus {
	counts: AlbumCounts;
	scan_status: unknown;
	recent_issues: AdminIssue[];
}

export interface AdminAlbumTreeNode {
	path: string;
	name?: string | undefined;
	children?: AdminAlbumTreeNode[] | undefined;
	[key: string]: unknown;
}

export interface TimeFolderHour {
	key: string;
	hour_start_ms: number | null;
	media_count: number;
}

export interface TimeFolderDay {
	key: string;
	day_start_ms: number | null;
	media_count: number;
	hours: TimeFolderHour[];
}

export interface TimeFoldersPayload {
	days: TimeFolderDay[];
	timezone_offset_minutes: number;
	stale: boolean;
}

export interface AdminAlbumBrowseResponse {
	album: Record<string, unknown>;
	child_albums: Record<string, unknown>[];
	media: Record<string, unknown>[];
	page?: BrowsePageInfo | undefined;
	time_folders?: TimeFoldersPayload | null | undefined;
}

export interface BrowsePageInfo {
	limit?: number | null | undefined;
	offset: number;
	next_offset?: number | null | undefined;
	child_albums_returned: number;
	child_albums_total: number;
	media_returned: number;
	media_total: number;
}

export interface GalleryPageInfo {
	limit?: number | null | undefined;
	offset: number;
	next_offset?: number | null | undefined;
	media_returned: number;
	media_total: number;
}

export interface VirtualGroupSummary {
	key: string;
	label: string;
	media_count: number;
	first_timestamp: number | null;
	last_timestamp: number | null;
	cover_visible_path: string | null;
}

export interface VirtualGroupsResponse {
	album: Record<string, unknown>;
	groups: VirtualGroupSummary[];
	enabled: boolean;
	media_total: number;
	generation: number | null;
	rebuilding: boolean;
	timezone: string;
	min_media: number;
	target_group_size: number;
	max_group_size: number;
	max_gap_seconds: number;
}

export interface VirtualGroupMediaResponse {
	group: VirtualGroupSummary;
	media: Record<string, unknown>[];
	next_cursor: string | null;
	generation: number;
}

export interface AdminGalleryResponse {
	album: Record<string, unknown>;
	media: Record<string, unknown>[];
	page: GalleryPageInfo;
	scan_status?: unknown;
}

export interface AlbumMapPoint {
	visible_path: string;
	album_path: string;
	name: string;
	media_kind: string;
	taken_at: number | null;
	latitude: number;
	longitude: number;
}

export interface AlbumMapBounds {
	min_latitude: number;
	max_latitude: number;
	min_longitude: number;
	max_longitude: number;
}

export interface AlbumMapResponse {
	path: string;
	point_count: number;
	total_count: number;
	truncated: boolean;
	bounds: AlbumMapBounds | null;
	points: AlbumMapPoint[];
}

export interface AdminIssue {
	[key: string]: unknown;
}

export interface AdminApiKey {
	id: string | number;
	label?: string | undefined;
	[key: string]: unknown;
}

export interface AdminShare {
	id: string | number;
	share_token?: string | undefined;
	share_kind?: string | undefined;
	visible_path?: string | undefined;
	title?: string | undefined;
	share_url?: string | undefined;
	revoked_at?: number | null | undefined;
	upload_enabled?: boolean | undefined;
	upload_max_files?: number | null | undefined;
	upload_max_total_bytes?: number | null | undefined;
	upload_max_item_bytes?: number | null | undefined;
	[key: string]: unknown;
}

export interface ShareUploadPolicy {
	enabled: boolean;
	max_files: number;
	max_total_bytes: number;
	max_item_bytes: number;
}

export interface CreatedApiKeyResponse {
	key?: AdminApiKey | undefined;
	token?: string | undefined;
	plain_token?: string | undefined;
	secret?: string | undefined;
	api_key?: string | undefined;
	[key: string]: unknown;
}

export interface CreatedSubsetResponse {
	subset_path?: string | undefined;
	link_path?: string | undefined;
	target_path?: string | undefined;
	link_paths?: string[] | undefined;
	target_paths?: string[] | undefined;
	skipped_target_paths?: string[] | undefined;
	created_new_subset?: boolean | undefined;
	scan_queued?: boolean | undefined;
	[key: string]: unknown;
}

export interface PublicShareResponse {
	share: Record<string, unknown>;
	current_path: string;
	album?: Record<string, unknown> | null | undefined;
	child_albums: Record<string, unknown>[];
	media: Record<string, unknown>[];
	media_detail?: Record<string, unknown> | null | undefined;
	page?: GalleryPageInfo | null | undefined;
	time_folders?: TimeFoldersPayload | null | undefined;
}

export const DEFAULT_STATIC_CONFIG: StaticConfig = {
	app_name: 'Albumz',
	domain: 'http://localhost:8000',
	api: 'http://localhost:8000',
	debug: false,
	library_name: 'Library'
};

function normalizeConfig(value: Partial<StaticConfig> | null | undefined): StaticConfig {
	return {
		app_name: value?.app_name || DEFAULT_STATIC_CONFIG.app_name,
		domain: value?.domain || DEFAULT_STATIC_CONFIG.domain,
		api: value?.api || DEFAULT_STATIC_CONFIG.api,
		debug: Boolean(value?.debug),
		library_name: value?.library_name || DEFAULT_STATIC_CONFIG.library_name
	};
}

export async function loadStaticConfig(fetchFn: typeof fetch = fetch): Promise<StaticConfig> {
	try {
		const response = await fetchFn('/static/config.json', {
			headers: { accept: 'application/json' }
		});

		if (!response.ok) {
			return normalizeConfig(DEFAULT_STATIC_CONFIG);
		}

		const text = await response.text();
		if (!text.trim()) {
			return normalizeConfig(DEFAULT_STATIC_CONFIG);
		}

		try {
			return normalizeConfig(JSON.parse(text) as Partial<StaticConfig>);
		} catch {
			return normalizeConfig(DEFAULT_STATIC_CONFIG);
		}
	} catch {
		return normalizeConfig(DEFAULT_STATIC_CONFIG);
	}
}

async function readJson<T>(response: Response): Promise<T> {
	if (!response.ok) {
		let message = `${response.status} ${response.statusText}`;

		try {
			const payload = (await response.json()) as Record<string, unknown>;
			const detail = payload.message ?? payload.error ?? payload.detail;

			if (typeof detail === 'string' && detail.trim()) {
				message = detail;
			}
		} catch {
			// ignore non-JSON error bodies
		}

		throw new Error(message);
	}

	if (response.status === 204) {
		return undefined as T;
	}

	const contentLength = response.headers.get('content-length');
	if (contentLength === '0') {
		return undefined as T;
	}

	const text = await response.text();
	if (!text.trim()) {
		return undefined as T;
	}

	return JSON.parse(text) as T;
}

export const API_REQUEST_TIMEOUT_MS = 30_000;

function requestSignal(init: RequestInit, timeoutMs: number): AbortSignal | undefined {
	const caller = init.signal ?? undefined;
	if (timeoutMs <= 0 || typeof AbortSignal.timeout !== 'function') {
		return caller;
	}
	const timeout = AbortSignal.timeout(timeoutMs);
	if (!caller) {
		return timeout;
	}
	const controller = new AbortController();
	if (caller.aborted) {
		controller.abort(caller.reason);
	} else {
		caller.addEventListener('abort', () => controller.abort(caller.reason), { once: true });
	}
	timeout.addEventListener('abort', () => controller.abort(timeout.reason), { once: true });
	return controller.signal;
}

function isTimeoutError(error: unknown): boolean {
	return error instanceof DOMException && error.name === 'TimeoutError';
}

export async function apiRequest<T>(
	path: string,
	init: RequestInit = {},
	fetchFn: typeof fetch = fetch,
	timeoutMs: number = API_REQUEST_TIMEOUT_MS
): Promise<T> {
	const headers = new Headers(init.headers);
	headers.set('accept', 'application/json');

	if (init.body && !headers.has('content-type')) {
		headers.set('content-type', 'application/json');
	}

	const signal = requestSignal(init, timeoutMs);
	const requestInit: RequestInit = { ...init, headers };
	if (signal) {
		requestInit.signal = signal;
	}

	try {
		const response = await fetchFn(path, requestInit);

		return await readJson<T>(response);
	} catch (error) {
		if (isTimeoutError(error)) {
			throw new Error('The server took too long to respond. Try a narrower view or retry.', {
				cause: error
			});
		}
		throw error;
	}
}

export function postJson<T>(
	path: string,
	body: unknown,
	fetchFn: typeof fetch = fetch
): Promise<T> {
	return apiRequest<T>(
		path,
		{
			method: 'POST',
			body: JSON.stringify(body)
		},
		fetchFn
	);
}
